import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';

/**
 * BeFair Custom Tooltip Component
 *
 * A modern, flexible tooltip component with two display modes:
 * - Simple: Text-only tooltip with gradient background
 * - Card: Rich tooltip with icon, title, description, and action button
 *
 * @example
 * // Simple tooltip
 * <button [appBefairTooltip]="'Save changes'">Save</button>
 *
 * // Rich card tooltip
 * <button
 *   [appBefairTooltip]="''"
 *   tooltipTitle="Pro Tip"
 *   tooltipDescription="Use Ctrl+S for quick save"
 *   tooltipIcon="lightbulb">
 *   Help
 * </button>
 */
@Component({
  selector: 'app-befair-tooltip',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  templateUrl: './custom-tooltip.component.html',
  styleUrl: './custom-tooltip.component.scss',
  host: {
    '[attr.role]': '"tooltip"',
    '[attr.aria-live]': '"polite"'
  }
})
export class CustomTooltipComponent {
  // Input properties for tooltip content
  @Input() text = '';
  @Input() icon?: string;
  @Input() title?: string;
  @Input() description?: string;
  @Input() actionText?: string;
  @Input() actionCallback?: () => void;

  /**
   * Check if tooltip is in simple mode (text only)
   */
  get isSimpleMode(): boolean {
    return !this.title && !this.description && !this.actionText;
  }

  /**
   * Check if action button should be displayed
   */
  get hasAction(): boolean {
    return !!(this.actionText && this.actionCallback);
  }

  /**
   * Handle action button click
   */
  onActionClick(): void {
    if (this.actionCallback) {
      this.actionCallback();
    }
  }
}
